import { ActivityLevel } from "@/clients/models/ActivityLevel";
import { QueryKeys } from "@/constants/query-keys";
import { useGetActivityLevel } from "@/hooks/requests/acivity/use-get-activity-level";
import { useUpdateActivityLevel } from "@/hooks/requests/acivity/use-update-activity-level";
import { useToast } from "@/hooks/ui/use-toast";
import { setProfileContext } from "@/utils/sentry/tools";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

export interface ActivityLevelOption {
  value: ActivityLevel.activityLevel;
  title: string;
  description: string;
}

export const activityLevelOptions: ActivityLevelOption[] = [
  {
    value: ActivityLevel.activityLevel.SEDENTARY_NO_EXERCISE,
    title: "Sedentary",
    description: "(little to no exercise)",
  },
  {
    value: ActivityLevel.activityLevel.LIGHTLY_ACTIVE_EXERCISE_1_3_DAYS_WEEK,
    title: "Lightly Active",
    description: "(light exercise 1 to 3 days/week)",
  },
  {
    value: ActivityLevel.activityLevel.MODERATELY_ACTIVE_EXERCISE_3_5_DAYS_WEEK,
    title: "Moderately Active",
    description: "(light to moderate exercise 4 to 5 days / week)",
  },
  {
    value: ActivityLevel.activityLevel.VERY_ACTIVE_EXERCISE_6_7_DAYS_A_WEEK,
    title: "Active",
    description:
      "(moderate exercise 6 to 7 days / week or intense exercise 3 to 4 times / week)",
  },
  {
    value:
      ActivityLevel.activityLevel
        .EXTRA_ACTIVE_VERY_HARD_EXERCISE_AND_PHYSICAL_JOB,
    title: "Very Active",
    description:
      "(intense exercise 6 to 7 times / week, sports training or physical job)",
  },
];

interface UseActivityLevelProps {
  onSuccess?: () => void;
}

export const useActivityLevel = ({ onSuccess }: UseActivityLevelProps) => {
  const queryClient = useQueryClient();
  const { activityLevel, isLoading: isLoadingActivityLevel } =
    useGetActivityLevel();
  const { updateActivityLevel, isUpdating } = useUpdateActivityLevel();
  const { showError } = useToast();

  const [selectedActivityLevel, setSelectedActivityLevel] =
    useState<ActivityLevel.activityLevel | null>(null);

  useEffect(() => {
    if (activityLevel?.activityLevel) {
      setSelectedActivityLevel(activityLevel.activityLevel);
    }
  }, [activityLevel]);

  const handleActivityLevelSelect = useCallback(
    (option: ActivityLevelOption) => {
      return () => {
        console.log("selected", option.value);
        setSelectedActivityLevel(option.value);
      };
    },
    [],
  );

  const handleSubmit = () => {
    if (!selectedActivityLevel) {
      showError("Please select an activity level");
      return;
    }

    const dataBody: ActivityLevel = {
      activityLevel: selectedActivityLevel,
    };

    updateActivityLevel(dataBody, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QueryKeys.ACTIVITY_LEVEL] });
        setProfileContext(QueryKeys.ACTIVITY_LEVEL, dataBody);
        onSuccess?.();
      },
      onError: (error) => {
        showError(error.message);
      },
    });
  };

  return {
    selectedActivityLevel,
    isLoading: isLoadingActivityLevel,
    handleActivityLevelSelect,
    handleSubmit,
    isUpdating,
    options: activityLevelOptions,
  };
};
